/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  Container,
  Group,
  Title,
  Text,
  Button,
  SimpleGrid,
  Card,
  Stack,
  Badge,
  Modal,
  Textarea,
  Paper,
  Box,
  Grid,
} from "@mantine/core";

import { showNotification } from "@mantine/notifications";
import {
  IconCalendarEvent,
  IconClock,
  IconPlus,
  IconList,
  IconChartBar,
  IconEye,
  IconSearch,
  IconEdit,
  IconX,
  IconCheck,
} from "@tabler/icons-react";
import MeetingForm from "./component/createMeeting/createMeetingForm";
import MeetingDetailsModal from "./component/meetingDetail/meetingDetailModal";
import AllMeetings from "./component/allMeeting/allMeeting";
import RespondToInvitationModal from "./component/invitationModal/invitationModal";
import TodayMeetings from "./component/todayMeeting/todayMeeting";
import CancelMeetingModal from "./component/cancelModal/cancelModal";
import { PermissionButton } from "api/permission/buttonpermission";

type Attendee = {
  user: string;
  userType: string;
  status?: "pending" | "accepted" | "declined";
};

export type Meeting = {
  id: string;
  title: string;
  description?: string;
  scheduledDate: string;
  duration: number;
  status: "scheduled" | "ongoing" | "completed" | "cancelled";
  meetingPlatform?: string;
  meetingLink?: string;
  attendees: Attendee[];
  agenda?: string;
  projectId?: string;
  isRecurring?: boolean;
};

const initialMeetings: Meeting[] = [
  {
    id: "123",
    title: "Project Kickoff Meeting",
    description: "Initial meeting to discuss project requirements and timeline",
    scheduledDate: new Date().toISOString(),
    duration: 60,
    status: "scheduled",
    meetingPlatform: "zoom",
    meetingLink: "https://zoom.us/j/123456789",
    attendees: [
      {
        user: "507f1f77bcf86cd799439011",
        userType: "User",
        status: "accepted",
      },
      {
        user: "507f1f77bcf86cd799439012",
        userType: "Client",
        status: "pending",
      },
    ],
    agenda:
      "1. Project overview\n2. Requirements discussion\n3. Timeline planning",
  },
];

export default function MeetingManagement(): JSX.Element {
  const [activeTab, setActiveTab] = useState<
    "dashboard" | "today" | "all" | "create"
  >("dashboard");
  const [meetings, setMeetings] = useState<Meeting[]>(initialMeetings);

  const [openedMeetingModal, setOpenedMeetingModal] = useState(false);
  const [openedResponseModal, setOpenedResponseModal] = useState(false);
  const [openedCancelModal, setOpenedCancelModal] = useState(false);
  const [currentMeetingId, setCurrentMeetingId] = useState<string | null>(null);

  const [filterStart, setFilterStart] = useState<string | null>(null);
  const [filterEnd, setFilterEnd] = useState<string | null>(null);

  const todayStr = useMemo(() => new Date().toDateString(), []);

  useEffect(() => {
    const today = new Date();
    const nextWeek = new Date(today);
    nextWeek.setDate(nextWeek.getDate() + 7);
    setFilterStart(today.toISOString().slice(0, 10));
    setFilterEnd(nextWeek.toISOString().slice(0, 10));
  }, []);

  function joinMeeting(meeting: Meeting) {
    showNotification({ message: "Joining meeting...", color: "blue" });
    if (meeting.meetingLink) {
      window.open(meeting.meetingLink, "_blank");
    }
  }

  function viewMeeting(meetingId: string) {
    setCurrentMeetingId(meetingId);
    setOpenedMeetingModal(true);
  }

  function respondToMeeting(response: "accepted" | "declined") {
    if (!currentMeetingId) return;
    setMeetings((s) =>
      s.map((m) =>
        m.id === currentMeetingId
          ? {
              ...m,
              attendees: m.attendees.map((a) => ({
                ...a,
                status: response === "accepted" ? "accepted" : "declined",
              })),
            }
          : m
      )
    );
    showNotification({
      message: `Meeting invitation ${response}`,
      color: response === "accepted" ? "green" : "red",
    });
    setOpenedResponseModal(false);
    setOpenedMeetingModal(false);
  }

  return (
    <Box>
      <Paper
        radius="md"
        p="xl"
        shadow="xl"
        style={{ backdropFilter: "blur(8px)" }}
      >
        <Group position="apart" align="flex-start">
          <div>
            <Title
              order={1}
              style={{
                background: "linear-gradient(135deg,#667eea,#764ba2)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              <IconCalendarEvent
                size={28}
                style={{
                  display: "inline-block",
                  verticalAlign: "middle",
                  marginRight: 8,
                }}
              />{" "}
              Meeting Management System
            </Title>
            <Text color="dimmed">
              Streamline your meetings with intelligent scheduling and
              management
            </Text>
          </div>
        </Group>
      </Paper>
      <Paper
        radius="md"
        p="xl"
        shadow="xl"
        mt={12}
        style={{ backdropFilter: "blur(8px)" }}
      >
        <Group>
          <Button
            variant={activeTab === "dashboard" ? "filled" : "subtle"}
            onClick={() => setActiveTab("dashboard")}
            leftIcon={<IconChartBar />}
          >
            Dashboard
          </Button>
          <Button
            variant={activeTab === "today" ? "filled" : "subtle"}
            onClick={() => setActiveTab("today")}
            leftIcon={<IconClock />}
          >
            Today's Meetings
          </Button>
          <Button
            variant={activeTab === "all" ? "filled" : "subtle"}
            onClick={() => setActiveTab("all")}
            leftIcon={<IconList />}
          >
            All Meetings
          </Button>
          <PermissionButton
            permission="connect.meeting_management.create"
            variant={activeTab === "create" ? "filled" : "outline"}
            onClick={() => setActiveTab("create")}
            leftIcon={<IconPlus />}
          >
            Create Meeting
          </PermissionButton>
          {/* <Button
            variant={activeTab === "create" ? "filled" : "outline"}
            onClick={() => setActiveTab("create")}
            leftIcon={<IconPlus />}
          >
            Create Meeting
          </Button> */}
        </Group>
      </Paper>
      {/* <Container size="xl" mt={12}> */}
      {activeTab === "dashboard" && (
        <Stack>
          <SimpleGrid
            cols={4}
            mt={10}
            breakpoints={[
              { maxWidth: "md", cols: 4 },
              { maxWidth: "sm", cols: 1 },
            ]}
          >
            <Card
              shadow="xl"
              radius="md"
              p="lg"
              withBorder
              style={{ background: "rgba(255,255,255,0.95)" }}
            >
              <Group position="apart" align="start">
                <Group>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 12,
                      display: "grid",
                      placeItems: "center",
                      background: "linear-gradient(135deg,#667eea,#764ba2)",
                      color: "white",
                    }}
                  >
                    <IconClock />
                  </Box>
                  <div>
                    <Text weight={700}>Today's Schedule</Text>
                    <Text color="dimmed" size="sm">
                      View your meetings for today
                    </Text>
                  </div>
                </Group>
                <PermissionButton
                  permission="connect.meeting_management.view"
                  onClick={() => setActiveTab("today")}
                  leftIcon={<IconEye />}
                >
                  View Today's Meetings
                </PermissionButton>
                {/* <Button
                  onClick={() => setActiveTab("today")}
                  leftIcon={<IconEye />}
                >
                  View Today's Meetings
                </Button> */}
              </Group>
            </Card>

            <Card
              shadow="xl"
              radius="md"
              p="lg"
              withBorder
              style={{ background: "rgba(255,255,255,0.95)" }}
            >
              <Group position="apart" align="start">
                <Group>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 12,
                      display: "grid",
                      placeItems: "center",
                      background: "linear-gradient(135deg,#4facfe,#00f2fe)",
                      color: "white",
                    }}
                  >
                    <IconPlus />
                  </Box>
                  <div>
                    <Text weight={700}>Schedule Meeting</Text>
                    <Text color="dimmed" size="sm">
                      Create a new meeting
                    </Text>
                  </div>
                </Group>
                <PermissionButton
                  permission="connect.meeting_management.create"
                  onClick={() => setActiveTab("create")}
                  leftIcon={<IconCalendarEvent />}
                >
                  Create Meeting
                </PermissionButton>
              </Group>
            </Card>

            <Card
              shadow="xl"
              radius="md"
              p="lg"
              withBorder
              style={{ background: "rgba(255,255,255,0.95)" }}
            >
              <Group position="apart" align="start">
                <Group>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 12,
                      display: "grid",
                      placeItems: "center",
                      background: "linear-gradient(135deg,#a8edea,#fed6e3)",
                      color: "white",
                    }}
                  >
                    <IconList />
                  </Box>
                  <div>
                    <Text weight={700}>All Meetings</Text>
                    <Text color="dimmed" size="sm">
                      Browse and manage meetings
                    </Text>
                  </div>
                </Group>
                <Button
                  onClick={() => setActiveTab("all")}
                  leftIcon={<IconSearch />}
                >
                  Browse Meetings
                </Button>
              </Group>
            </Card>

            <Card
              shadow="xl"
              radius="md"
              p="lg"
              withBorder
              style={{ background: "rgba(255,255,255,0.95)" }}
            >
              <Group>
                <Group>
                  <Box
                    sx={{
                      width: 52,
                      height: 52,
                      borderRadius: 12,
                      display: "grid",
                      placeItems: "center",
                      background: "linear-gradient(135deg,#ffecd2,#fcb69f)",
                      color: "white",
                    }}
                  >
                    <IconChartBar />
                  </Box>
                  <div>
                    <Text weight={700}>Quick Stats</Text>
                    <Text color="dimmed" size="sm">
                      Meeting overview
                    </Text>
                  </div>
                </Group>
                <SimpleGrid cols={2} style={{ marginTop: 12 }}>
                  <Paper p="md">
                    <Text weight={800} size="xl">
                      {meetings.length}
                    </Text>
                    <Text color="dimmed" size="xs">
                      This Week
                    </Text>
                  </Paper>
                  <Paper p="md">
                    <Text weight={800} size="xl">
                      {
                        meetings.filter(
                          (m) =>
                            new Date(m.scheduledDate).toDateString() ===
                            new Date().toDateString()
                        ).length
                      }
                    </Text>
                    <Text color="dimmed" size="xs">
                      Today
                    </Text>
                  </Paper>
                </SimpleGrid>
              </Group>
            </Card>
          </SimpleGrid>
        </Stack>
      )}

      {activeTab === "today" && (
        <TodayMeetings onJoin={joinMeeting} onView={viewMeeting} />
      )}

      {activeTab === "all" && <AllMeetings />}

      {activeTab === "create" && <MeetingForm />}

      <Modal
        opened={openedResponseModal}
        onClose={() => setOpenedResponseModal(false)}
        title="Respond to Meeting Invitation"
      >
        <Stack align="center">
          <Text color="dimmed">Please respond to the meeting invitation:</Text>
          <Group>
            <Button
              onClick={() => respondToMeeting("accepted")}
              leftIcon={<IconCheck />}
            >
              Accept
            </Button>
            <Button
              color="red"
              onClick={() => respondToMeeting("declined")}
              leftIcon={<IconX />}
            >
              Decline
            </Button>
          </Group>
        </Stack>
      </Modal>
      <RespondToInvitationModal
        opened={openedResponseModal}
        onClose={() => setOpenedResponseModal(false)}
        onRespond={respondToMeeting}
      />
      {/* </Container> */}
    </Box>
  );
}
